import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Alert, Snackbar, SnackbarCloseReason } from "@mui/material";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

import { tokens } from "../../theme/tokens";

type ToastSeverity = "success" | "error" | "info";

/** Show a short message at the bottom of the screen; it fades after about 4 seconds. */
export interface ToastApi {
  success: (message: React.ReactNode) => void;
  error: (message: React.ReactNode) => void;
  info: (message: React.ReactNode) => void;
}

interface ToastItem {
  key: number;
  severity: ToastSeverity;
  message: React.ReactNode;
}

const AUTO_HIDE_MS = 4000;

const toneFor: Record<ToastSeverity, "ok" | "err" | "info"> = {
  success: "ok",
  error: "err",
  info: "info",
};

const iconFor: Record<ToastSeverity, React.ReactNode> = {
  success: <CheckCircleOutline fontSize="inherit" />,
  error: <ErrorOutline fontSize="inherit" />,
  info: <InfoOutlined fontSize="inherit" />,
};

const ToastContext = createContext<ToastApi | null>(null);

let nextKey = 1;

/**
 * Toasts for success and information instead of a dialog (AB#6872, "Dialogs").
 * Mounted once in App.tsx; call `useToast()` anywhere below it. Messages queue and
 * show one at a time, bottom-centre, on an inverse surface that suits both modes.
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [queue, setQueue] = useState<ToastItem[]>([]);
  const [current, setCurrent] = useState<ToastItem | undefined>();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (queue.length && !current) {
      setCurrent(queue[0]);
      setQueue((prev) => prev.slice(1));
      setOpen(true);
    } else if (queue.length && current && open) {
      // A newer toast is waiting: close this one so the next can show.
      setOpen(false);
    }
  }, [queue, current, open]);

  const push = useCallback(
    (severity: ToastSeverity, message: React.ReactNode) => {
      setQueue((prev) => [...prev, { key: nextKey++, severity, message }]);
    },
    []
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push("success", message),
      error: (message) => push("error", message),
      info: (message) => push("info", message),
    }),
    [push]
  );

  const handleClose = (
    _event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        key={current?.key}
        open={open}
        autoHideDuration={AUTO_HIDE_MS}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionProps={{ onExited: () => setCurrent(undefined) }}
      >
        {current ? (
          <Alert
            severity={current.severity}
            variant="filled"
            icon={iconFor[current.severity]}
            onClose={() => setOpen(false)}
            role={current.severity === "error" ? "alert" : "status"}
            sx={(theme) => {
              // Inverse surface: the other mode's card and status colours.
              const inverse =
                tokens[theme.palette.mode === "dark" ? "light" : "dark"];
              return {
                alignItems: "center",
                minWidth: 280,
                maxWidth: 560,
                borderRadius: 1.5,
                border: "none",
                fontSize: "0.84375rem",
                backgroundColor: inverse.card,
                color: inverse.text.primary,
                boxShadow: theme.shadows[8],
                "& .MuiAlert-icon": {
                  color: inverse.status[toneFor[current.severity]].fg,
                  fontSize: 19,
                },
                "& .MuiAlert-action .MuiIconButton-root": {
                  color: inverse.text.secondary,
                },
              };
            }}
          >
            {current.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </ToastContext.Provider>
  );
}

/** `const toast = useToast(); toast.success("2 bills converted to checks");` */
export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used inside <ToastProvider>.");
  }
  return context;
}

export default ToastProvider;
